import { useState, useEffect, useCallback } from 'react';
import { api, formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, GitMerge, Edit3, ChevronDown, Dna, Search, AlertTriangle } from 'lucide-react';

const STATUS_COLORS = {
  pending_review: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  approved: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  rejected: 'bg-red-500/10 text-red-400 border-red-500/20',
  official: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  merged: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  deprecated: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
};

const STATUS_LABELS = {
  pending_review: 'Pendiente', approved: 'Aprobado', rejected: 'Rechazado',
  official: 'Oficial', merged: 'Fusionado', deprecated: 'Deprecado',
};

export default function AdminMorphReviewPage() {
  const [morphs, setMorphs] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: 'pending_review', sort: 'recent' });
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [mergeTarget, setMergeTarget] = useState('');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.getCategories().then(setCategories).catch(() => {});
  }, []);

  const loadMorphs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getAdminMorphs(filters);
      setMorphs(data.morphs || []);
      setStats(data.stats || {});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadMorphs(); }, [loadMorphs]);

  const handleAction = async (morph, action, extra = {}) => {
    try {
      await api.updateMorphStatus(morph.species_canonical, morph.canonical_slug, { action, ...extra });
      toast.success(`Morph ${action === 'approve' ? 'aprobado' : action === 'reject' ? 'rechazado' : action === 'merge' ? 'fusionado' : 'actualizado'}`);
      loadMorphs();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-morph-review" className="max-w-5xl">
      <div className="mb-6">
        <h1 className="text-2xl font-light tracking-tight text-zinc-100 font-[Outfit]">
          <Dna className="w-6 h-6 inline mr-2 text-emerald-400" />
          Revision de Morphs
        </h1>
        <p className="text-zinc-500 mt-1">Gestiona morphs user-generated y controla la taxonomia</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[
          { label: 'Pendientes', value: stats.pending, color: 'text-amber-400' },
          { label: 'Aprobados', value: stats.approved, color: 'text-emerald-400' },
          { label: 'Oficiales', value: stats.official, color: 'text-blue-400' },
          { label: 'Rechazados', value: stats.rejected, color: 'text-red-400' },
          { label: 'Total', value: stats.total, color: 'text-zinc-300' },
        ].map((s) => (
          <div key={s.label} className="glass-card p-3 text-center">
            <p className={`text-xl font-semibold ${s.color}`}>{s.value || 0}</p>
            <p className="text-xs text-zinc-500">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <Select value={filters.status || 'all'} onValueChange={(v) => setFilters(f => ({ ...f, status: v === 'all' ? undefined : v }))}>
          <SelectTrigger className="w-44 bg-white/5 border-white/10 text-zinc-100">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent className="bg-[#121215] border-white/10">
            <SelectItem value="all">Todos</SelectItem>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.sort || 'recent'} onValueChange={(v) => setFilters(f => ({ ...f, sort: v }))}>
          <SelectTrigger className="w-40 bg-white/5 border-white/10 text-zinc-100">
            <SelectValue placeholder="Ordenar" />
          </SelectTrigger>
          <SelectContent className="bg-[#121215] border-white/10">
            <SelectItem value="recent">Recientes</SelectItem>
            <SelectItem value="usage">Mas usados</SelectItem>
            <SelectItem value="oldest">Mas antiguos</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Morph List */}
      <div className="space-y-3">
        {loading ? (
          <p className="text-zinc-500 text-center py-12">Cargando...</p>
        ) : morphs.length === 0 ? (
          <p className="text-zinc-500 text-center py-12">No hay morphs que coincidan con los filtros</p>
        ) : (
          morphs.map((morph) => (
            <div key={`${morph.species_canonical}-${morph.canonical_slug}`}
              className="glass-card p-4" data-testid={`morph-row-${morph.canonical_slug}`}>
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-zinc-100 font-medium">{morph.display_name}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_COLORS[morph.status] || STATUS_COLORS.deprecated}`}>
                      {STATUS_LABELS[morph.status] || morph.status}
                    </span>
                    {morph.user_generated && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-zinc-500 border border-white/10">user-gen</span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500 mt-1 space-x-4">
                    <span>Especie: <span className="text-zinc-400">{morph.species_canonical}</span></span>
                    <span>Slug: <span className="text-zinc-400">{morph.canonical_slug}</span></span>
                    <span>Usos: <span className="text-zinc-400">{morph.usage_count || 0}</span></span>
                    {morph.users?.length > 0 && <span>Breeders: <span className="text-zinc-400">{morph.users.length}</span></span>}
                  </div>
                  {morph.similar_existing?.length > 0 && (
                    <div className="mt-2 flex items-center gap-2 px-3 py-1.5 rounded bg-amber-500/5 border border-amber-500/10">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                      <span className="text-xs text-amber-300">
                        Similar a: {morph.similar_existing.map(s => s.display_name).join(', ')}
                      </span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {morph.status === 'pending_review' && (
                    <>
                      <Button size="sm" variant="ghost" className="text-emerald-400 hover:bg-emerald-500/10"
                        onClick={() => handleAction(morph, 'approve')} data-testid={`approve-${morph.canonical_slug}`}>
                        <CheckCircle2 className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10"
                        onClick={() => handleAction(morph, 'reject')} data-testid={`reject-${morph.canonical_slug}`}>
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                  <Button size="sm" variant="ghost" className="text-purple-400 hover:bg-purple-500/10"
                    onClick={() => {
                      const target = prompt('Canonical slug del morph destino para fusionar:');
                      if (target) handleAction(morph, 'merge', { merge_with_canonical: target });
                    }}>
                    <GitMerge className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" className="text-zinc-400 hover:bg-white/5"
                    onClick={() => {
                      if (editingId === morph.canonical_slug) {
                        handleAction(morph, 'edit', { display_name: editName });
                        setEditingId(null);
                      } else {
                        setEditingId(morph.canonical_slug);
                        setEditName(morph.display_name);
                      }
                    }}>
                    <Edit3 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              {editingId === morph.canonical_slug && (
                <div className="mt-3 flex gap-2">
                  <Input value={editName} onChange={(e) => setEditName(e.target.value)}
                    className="bg-white/5 border-white/10 text-zinc-100 flex-1" placeholder="Nuevo nombre visible" />
                  <Button size="sm" onClick={() => { handleAction(morph, 'edit', { display_name: editName }); setEditingId(null); }}
                    className="bg-emerald-600 text-white">Guardar</Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancelar</Button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
